import React, { useEffect, useState } from 'react';
import { useGlobalContext } from 'GlobalContext';
import { ModalStackItem, useModal } from './ModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { shakeElement } from 'services/utils/shakeElement';
import { useTranslation } from 'react-i18next';

/*
 * GenericModal.tsx
 * Listens to the ModalContext to show modals, and handles the base
 * behaviour of modals such showing the modal title and close button,
 * handle the closing by clicking outside the modal or pressing escape, 
 * and make the modal scrollable if it gets too big for the screen.
 */

const GenericModal: React.FC = () => {
    const { t } = useTranslation();
    const { modalStack, closeModal } = useModal();
    const { hasUnsavedChanges, isAnyFieldEditing, errorMessages, setErrorMessages } = useGlobalContext();
    const [oversizedModals, setOversizedModals] = useState<Record<string, boolean>>({});

    // Track which modals are taller than the viewport to give them a fixed bottom margin
    useEffect(() => {
        const observers: ResizeObserver[] = [];

        // Determine the oversized state of every modal in the stack
        const updateOversizedState = () => {
            setOversizedModals(current => {
                const updated: Record<string, boolean> = {};
                let changed = Object.keys(current).length !== modalStack.length;

                // Compare the modal height against the viewport minus the backdrop padding
                modalStack.forEach(modal => {
                    const element = modal.modalRef?.current;
                    const isOversized = element ? element.offsetHeight > window.innerHeight - 48 : false;
                    updated[modal.key] = isOversized;
                    if (current[modal.key] !== isOversized) changed = true;
                });

                return changed ? updated : current;
            });
        };

        // Observe size changes of every modal element
        modalStack.forEach(modal => {
            const element = modal.modalRef?.current;
            if (element) {
                const observer = new ResizeObserver(updateOversizedState);
                observer.observe(element);
                observers.push(observer);
            }
        });

        // Update on window resizes and once directly
        window.addEventListener('resize', updateOversizedState);
        updateOversizedState();

        return () => {
            // Delete the observers and event listener
            observers.forEach(observer => observer.disconnect());
            window.removeEventListener('resize', updateOversizedState);
        };
    }, [modalStack]);

    // Handles mouse click outside the modal to close it
    const handleClickOutside = (event: React.MouseEvent<HTMLDivElement>, modalKey: string) => {
        if (event.currentTarget === event.target) {

            // Determine if the current modal or one of its fields has unsaved changes
            const modalHasUnsavedChanges = hasUnsavedChanges(modalKey);

            // If the modal has unsaved changes, shake the modal
            if (modalHasUnsavedChanges) {
                const modalElement = event.currentTarget.querySelector('.modal');

                if (modalElement) {
                    shakeElement(modalElement as HTMLElement);
                }
            
            // If the modal doesn't have unsaved changes, close the modal
            } else {
                if (errorMessages) {
                    setErrorMessages({});
                }

                closeModal();
            }
        }
    };
    
    // Handle the closing of the top modal after pressing escape, even if it has unsaved changes
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {

                // Get the key of the top modal in the modal stack
                const topModalKey = modalStack.at(-1)?.key;
                if (!topModalKey) return;

                // Skip closing when a field on the top modal is in edit mode, so escape closes that field first
                if (isAnyFieldEditing(topModalKey)) return;

                // If error messages exist, empty it
                if (errorMessages) {
                    setErrorMessages({})
                }

                // Close the modal
                closeModal();
            }
        };
        document.addEventListener('keydown', handleEscape);

        return () => {
            // Delete the event listeners
            document.removeEventListener('keydown', handleEscape);
        };
    }, [closeModal, modalStack, isAnyFieldEditing]);

    // Handle direct close of the modal
    const handleClose = () => {
        // If error messages exist, empty it
        if (errorMessages) {
            setErrorMessages({})
        }
        
        // Close the modal
        closeModal();
    }

    // Generate the backdrop class
    const generateBackdropClasses = (modal: ModalStackItem) => {
        return [
            'modal-backdrop',
            !modal.isOpen && 'hidden',
            modal.props?.modalPosition === 'center' && 'center',
        ]
        .filter(Boolean)
        .join(' ');
    };

    // Generate the modal class
    const generateModalClasses = (modal: ModalStackItem) => {
        return [
            'modal',
            !modal.isOpen && 'hidden',
            modal.props?.modalSize || 'small',
            modal.props?.modalSpacing || 'default',
            modal.props?.showSideTab && 'sidetab',
            oversizedModals[modal.key] && 'oversized',
        ]
        .filter(Boolean)
        .join(' ');
    };

    if (modalStack.length === 0) return null;

    return (
        <>
            {modalStack.map((modal) => (
                <div 
                    key={modal.key} 
                    className={generateBackdropClasses(modal)} 
                    data-modal-key={modal.key} 
                    onClick={(event) => handleClickOutside(event, modal.key)}
                >
                    <div className={generateModalClasses(modal)} 
                         ref={modal.modalRef}>
                        {modal.props?.title ? (
                            <div className='modal-header'>
                                <h4>{t(modal.props?.title)}</h4>
                                <FontAwesomeIcon 
                                    icon={faXmark} 
                                    className='modal-close' 
                                    onClick={handleClose} 
                                />
                            </div>
                        ) : (
                            <FontAwesomeIcon 
                                icon={faXmark} 
                                className='modal-close top-right' 
                                onClick={handleClose}
                            />
                        )}
                        {modal.content}
                    </div>
                </div>
            ))}
        </>
    );
};

export default GenericModal;